/**
 * Admin-only page to preview the welcome email template.
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { getEmailTemplatePreview } from '../services/api';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  margin-bottom: 0.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${(p) => p.theme?.secondaryText ?? '#718096'};
  margin-bottom: 1.5rem;
`;

const Card = styled.div`
  background: ${(p) => p.theme?.cardBg ?? p.theme?.secondaryBg ?? '#fff'};
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  font-size: 1rem;
  background: ${(p) => p.theme?.inputBg ?? '#fff'};
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  width: 100%;
  max-width: 200px;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  margin-bottom: 0.5rem;
`;

const PreviewFrame = styled.div`
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  border-radius: 0.25rem;
  padding: 1rem;
  background: #fff;
  color: #1a202c;
  min-height: 120px;
  overflow: auto;
  font-size: 0.95rem;
  line-height: 1.5;

  a {
    color: #3182ce;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

const TextPreview = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  font-size: 0.9rem;
  margin: 0;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
`;

const ErrorMessage = styled.p`
  color: #e53e3e;
  margin-top: 0.5rem;
`;

function AdminEmailPreviewPage() {
  const { theme } = useTheme();
  const [firstName, setFirstName] = useState('Sample');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEmailTemplatePreview(firstName)
      .then((data) => {
        if (!cancelled) {
          setPreview(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load preview');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [firstName]);

  return (
    <PageContainer>
      <PageTitle>Email preview</PageTitle>
      <PageSubtitle>
        Preview the welcome email template. Edit templates in <code>api/templates/</code>.
        To send subscribers the same content as a Discord post, use{' '}
        <strong>Social Media Mgmt</strong> → <em>Send email to subscribers</em> (see{' '}
        <code>NOTIFICATIONS.md</code>).
      </PageSubtitle>

      <Card theme={theme}>
        <SectionTitle theme={theme}>Welcome email preview</SectionTitle>
        <Label theme={theme}>Sample first name</Label>
        <Input
          theme={theme}
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Sample"
        />
      </Card>

      {loading && <p>Loading...</p>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {preview && !loading && (
        <>
          <Card theme={theme}>
            <SectionTitle theme={theme}>Subject</SectionTitle>
            <TextPreview theme={theme}>{preview.subject}</TextPreview>
          </Card>

          <Card theme={theme}>
            <SectionTitle theme={theme}>HTML preview</SectionTitle>
            <PreviewFrame
              theme={theme}
              dangerouslySetInnerHTML={{ __html: preview.html }}
            />
          </Card>

          <Card theme={theme}>
            <SectionTitle theme={theme}>Plain text</SectionTitle>
            <TextPreview theme={theme}>{preview.text}</TextPreview>
          </Card>
        </>
      )}
    </PageContainer>
  );
}

export default AdminEmailPreviewPage;
